import { createSlice, type Dispatch, type PayloadAction } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import { fetchWrapper, safeStorageSet } from '@utils'
import {
  API_URL,
  API_PORTAL_URL,
  DEFAULT_STRIPE_PK,
  DEFAULT_STRIPE_NAME,
  SESSION_STORAGE_TOKEN_KEY,
  COOKIE_UUID_KEY,
} from '@constants'
import { setShowLoader } from './loader'
import { setShowAlert } from './alert'
import { v4 as generateUuid } from 'uuid'
import Cookies from 'js-cookie'
import { setEventData } from './events'
import { setAnalyticsUserId, setAmplitudeUserProperties } from '@analytics'
import type { RootState } from '.'

export type PaymentMethodSettings = {
  paymentGateway: string | null
  stripePublishableKey: string | null
  stripeAccountName: string | null
  braintreeClientToken: string | null
}

export type PaymentMethodsState = {
  credit_card: PaymentMethodSettings | null
  paypal: PaymentMethodSettings | null
}

export const signupSlice = createSlice({
  name: 'signup',
  initialState: {
    uuid: '',
    email: '',
    name: '',
    emailValidity: false,
    token: '',
    btToken: '',
    deeplinkToken: '',
    deeplinkUrl: '',
    deeplinkQR: '',
    userId: '',
    isSuccess: false,
    stripe: {
      key: null as string | null,
      name: null as string | null,
    },
    revolutPay: {
      publishableKey: null,
      accountName: null,
    },
    blik: {
      publishableKey: null,
      accountName: null,
    },
    pix: {
      publishableKey: null,
      accountName: null,
    },
    amazonPay: {
      publishableKey: null as string | null,
      accountName: null as string | null,
    },
    paymentProvider: null as string | null,
    paymentMethods: {
      credit_card: null,
      paypal: null,
    } as PaymentMethodsState,
    paymentSettingsStatus: null as string | null,
    threeDS: 0,
    userLocation: null as string | null,
    userLocationResolved: false,
    tempPassword: '',
    subscriptions: null as unknown,
  },
  reducers: {
    setUserUuid: (state, action: PayloadAction<string>) => {
      state.uuid = action.payload
    },
    setEmail: (state, action) => {
      const { email, isValid } = action.payload
      state.email = email
      state.emailValidity = isValid
    },
    setName: (state, action: PayloadAction<string>) => {
      state.name = action.payload
    },
    setRequestResult: (state, action) => {
      const { access_token, id, subscriptions, email } = action.payload
      state.isSuccess = true
      state.token = access_token
      state.userId = id
      state.subscriptions = subscriptions
      state.email = email
    },
    setUserToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload
    },
    setPaymentSettingStatus: (state, action) => {
      state.paymentSettingsStatus = action.payload
    },
    setStripeData: (state, action) => {
      state.stripe = action.payload
    },
    setRevolutPaySettings: (state, action) => {
      state.revolutPay = action.payload
    },
    setBlikSettings: (state, action) => {
      state.blik = action.payload
    },
    setPixSettings: (state, action) => {
      state.pix = action.payload
    },
    setAmazonPaySettings: (state, action) => {
      state.amazonPay = action.payload
    },
    setBtToken: (state, action: PayloadAction<string>) => {
      state.btToken = action.payload
    },
    setPaymentProvider: (state, action) => {
      state.paymentProvider = action.payload
    },
    setPaymentMethods: (state, action: PayloadAction<PaymentMethodsState>) => {
      state.paymentMethods = action.payload
    },
    setThreeDS: (state, action: PayloadAction<number>) => {
      state.threeDS = action.payload
    },
    setUserLocation: (state, action: PayloadAction<string | null>) => {
      state.userLocation = action.payload
    },
    setUserLocationResolved: (state, action: PayloadAction<boolean>) => {
      state.userLocationResolved = action.payload
    },
    setDeeplinkData: (state, action) => {
      const { short_deeplink, qr_code } = action.payload
      state.deeplinkUrl = short_deeplink
      state.deeplinkQR = qr_code
    },
  },
})

export const {
  setUserUuid,
  setUserToken,
  setEmail,
  setName,
  setRequestResult,
  setPaymentSettingStatus,
  setPaymentProvider,
  setStripeData,
  setRevolutPaySettings,
  setBlikSettings,
  setPixSettings,
  setAmazonPaySettings,
  setBtToken,
  setPaymentMethods,
  setThreeDS,
  setUserLocation,
  setUserLocationResolved,
  setDeeplinkData,
} = signupSlice.actions

interface SignupParams {
  readonly email: string
  readonly user_uuid: string
  readonly landing_type: string
  readonly name?: string
}

interface SignupTokenParams {
  readonly token: string
  readonly user_uuid: string
  readonly landing_type: string
}

export const sendSignupRequest =
  ({ email, name, user_uuid, landing_type }: SignupParams) =>
  (dispatch: Dispatch) => {
    dispatch(setShowLoader(true))

    const params = {
      registration_source: 'email',
      email,
      user_uuid,
      landing_type,
      name: name || email,
    }

    return fetchWrapper(`${API_URL}/auth/signup`, { body: params })
      .then((data) => {
        const { access_token, user } = data.data
        safeStorageSet(SESSION_STORAGE_TOKEN_KEY, access_token)
        dispatch(setRequestResult({ access_token, ...user }))
        dispatch(getUserLocation())
        getPaymentSettings(access_token)(dispatch)
        setAnalyticsUserId(user.uuid)
      })
      .catch((error) => {
        dispatch(setShowLoader(false))
        if (error.status !== 409) {
          dispatch(setShowAlert(error.errors?.messages || 'Failed to create an account'))
        }
        throw error
      })
  }

export const getUserLocation = (): any => (dispatch: Dispatch) =>
  fetchWrapper(`${API_PORTAL_URL}/cf-country`)
    .then((data) => {
      const countryCode = data?.country_code || null
      dispatch(setUserLocation(countryCode))
      dispatch(setUserLocationResolved(true))
      return countryCode
    })
    .catch((error) => {
      console.error(error)
      dispatch(setUserLocationResolved(true))
      return null
    })

export const getPaymentSettings =
  (token: string, attemptNum: number = 0) =>
  (dispatch: Dispatch) => {
    let stripeParams: Record<string, string> = {
        key: DEFAULT_STRIPE_PK,
        name: DEFAULT_STRIPE_NAME,
      },
      status: null | string = null,
      paymentGateway: null | string = null

    fetchWrapper(`${API_PORTAL_URL}/users/me/payments-settings`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((data) => {
        const {
          stripe_publishable_key,
          stripe_account_name,
          braintree_client_token,
          payment_gateway,
          request_three_d_secure,
          payment_methods,
        } = data
        const creditCardPaymentSettings = payment_methods?.credit_card
        const paypalPaymentSettings = payment_methods?.paypal

        const resolvedCreditCardStripePk =
          creditCardPaymentSettings?.stripe_publishable_key || stripe_publishable_key
        const resolvedCreditCardStripeAccountName =
          creditCardPaymentSettings?.stripe_account_name || stripe_account_name
        const resolvedCreditCardBraintreeToken =
          creditCardPaymentSettings?.braintree_client_token || braintree_client_token || null

        const revolutMethod = payment_methods?.revolut_pay
        if (revolutMethod) {
          dispatch(
            setRevolutPaySettings({
              publishableKey: revolutMethod.stripe_publishable_key || null,
              accountName: revolutMethod.account_name || null,
            }),
          )
        }
        const blikMethod = payment_methods?.blik
        dispatch(
          setBlikSettings({
            publishableKey: blikMethod?.stripe_publishable_key || null,
            accountName: blikMethod?.account_name || blikMethod?.stripe_account_name || null,
          }),
        )
        const pixMethod = payment_methods?.pix_automatico
        dispatch(
          setPixSettings({
            publishableKey: pixMethod?.stripe_publishable_key || null,
            accountName: pixMethod?.account_name || pixMethod?.stripe_account_name || null,
          }),
        )
        const amazonPayMethod = payment_methods?.amazon_pay
        dispatch(
          setAmazonPaySettings({
            publishableKey: amazonPayMethod?.stripe_publishable_key || null,
            accountName:
              amazonPayMethod?.account_name || amazonPayMethod?.stripe_account_name || null,
          }),
        )
        paymentGateway = payment_gateway || 'stripe'
        dispatch(setBtToken(resolvedCreditCardBraintreeToken))
        dispatch(
          setPaymentMethods({
            credit_card: {
              paymentGateway: creditCardPaymentSettings?.payment_gateway || paymentGateway,
              stripePublishableKey: resolvedCreditCardStripePk || null,
              stripeAccountName: resolvedCreditCardStripeAccountName || null,
              braintreeClientToken: resolvedCreditCardBraintreeToken,
            },
            paypal: {
              paymentGateway: paypalPaymentSettings?.payment_gateway || paymentGateway,
              stripePublishableKey:
                paypalPaymentSettings?.stripe_publishable_key || stripe_publishable_key || null,
              stripeAccountName: paypalPaymentSettings?.account_name || stripe_account_name || null,
              braintreeClientToken:
                paypalPaymentSettings?.braintree_client_token || braintree_client_token || null,
            },
          }),
        )
        dispatch(setThreeDS(request_three_d_secure || 0))
        status = 'success'
        if (resolvedCreditCardStripePk) {
          stripeParams = {
            key: resolvedCreditCardStripePk,
            name: resolvedCreditCardStripeAccountName,
          }
        }
      })
      .catch((error) => {
        console.log(error)
        if (attemptNum < 2) {
          setTimeout(() => getPaymentSettings(token, ++attemptNum)(dispatch), 10000)
        } else {
          status = 'fail'
          paymentGateway = 'stripe'
        }
      })
      .then(() => {
        dispatch(setPaymentProvider(paymentGateway))
        dispatch(setStripeData(stripeParams))
        dispatch(setPaymentSettingStatus(status))
      })
      .then(() => dispatch(setShowLoader(false)))
  }

export const sendSignupTokenRequest =
  ({ token, user_uuid, landing_type }: SignupTokenParams) =>
  (dispatch: Dispatch) => {
    dispatch(setShowLoader(true))

    const params = {
      token,
      landing_type,
      user_uuid,
      auth_type: 'signup',
    }

    return fetchWrapper(`${API_URL}/auth/social/google`, {
      body: params,
    })
      .then((data) => {
        const { access_token, user } = data.data
        safeStorageSet(SESSION_STORAGE_TOKEN_KEY, access_token)
        dispatch(setRequestResult({ access_token, ...user }))
        dispatch(getUserLocation())
        getPaymentSettings(access_token)(dispatch)
        setAnalyticsUserId(user.uuid)
      })
      .catch((error) => {
        dispatch(setShowLoader(false))
        if (error.status !== 409) {
          dispatch(setShowAlert(error.errors?.messages || 'Failed to create an account'))
        }
        throw error
      })
  }

export const getUserInfo = (token: string) => (dispatch: Dispatch) => {
  fetchWrapper(`${API_URL}/users/me`, { headers: { Authorization: `Bearer ${token}` } })
    .then((data) => {
      safeStorageSet(SESSION_STORAGE_TOKEN_KEY, token)
      setUuid(data.data.uuid)(dispatch)
      dispatch(setRequestResult({ ...data.data, access_token: token }))
      dispatch(setEmail({ email: data.data.email, isValid: true }))
      getPaymentSettings(token)(dispatch)
      setAnalyticsUserId(data.data.uuid)
    })
    .catch((error) => console.error(error))
}

export const sendEmailConsent = (email_consent: boolean, token: string) =>
  fetchWrapper(`${API_PORTAL_URL}/users/me/email-consent`, {
    body: { consent: email_consent },
    headers: { Authorization: `Bearer ${token}` },
  }).catch((error) => {
    Sentry.captureException(error, {
      tags: { source: 'email_consent' },
      extra: { consent: email_consent },
    })
  })

export const setUuid =
  (userUuid: string = '') =>
  (dispatch: Dispatch) => {
    const cookieUuid = Cookies.get(COOKIE_UUID_KEY)
    const generatedUuid = generateUuid()
    const uuid = userUuid || cookieUuid || generatedUuid

    if (!cookieUuid) Cookies.set(COOKIE_UUID_KEY, uuid, { expires: 365 })
    dispatch(setUserUuid(uuid))
    dispatch(setEventData({ userUuid: uuid }))
    setAmplitudeUserProperties({ uuid })
    setAnalyticsUserId(uuid)
  }

export const selectIsSuccess = (state: RootState) => state.signup.isSuccess
export const selectPaymentSettingsStatus = (state: RootState) => state.signup.paymentSettingsStatus
export const selectEmail = (state: RootState) => state.signup.email
export const selectName = (state: RootState) => state.signup.name
export const selectEmailValidity = (state: RootState) => state.signup.emailValidity
export const selectUuid = (state: RootState) => state.signup.uuid
export const selectToken = (state: RootState) => state.signup.token
export const selectPaymentProvider = (state: RootState) => state.signup.paymentProvider
export const selectPaymentMethods = (state: RootState) => state.signup.paymentMethods
export const selectStripe = (state: RootState) => state.signup.stripe
export const selectRevolutPaySettings = (state: RootState) => state.signup.revolutPay
export const selectBlikSettings = (state: RootState) => state.signup.blik
export const selectPixSettings = (state: RootState) => state.signup.pix
export const selectAmazonPaySettings = (state: RootState) => state.signup.amazonPay
export const selectBtToken = (state: RootState) => state.signup.btToken
export const selectDeeplinkToken = (state: RootState) => state.signup.deeplinkToken
export const selectDeeplinkUrl = (state: RootState) => state.signup.deeplinkUrl
export const selectDeeplinkQR = (state: RootState) => state.signup.deeplinkQR
export const selectThreeDS = (state: RootState) => state.signup.threeDS
export const selectUserLocation = (state: RootState) => state.signup.userLocation
export const selectUserLocationResolved = (state: RootState) => state.signup.userLocationResolved
export const selectIsNonUsCountry = (state: RootState) => {
  const countryCode = state.signup.userLocation
  return Boolean(countryCode && countryCode.toUpperCase() !== 'US')
}
export const selectIsUsCountry = (state: RootState) =>
  state.signup.userLocation?.toUpperCase() === 'US'
export const selectTempPassword = (state: RootState) => state.signup.tempPassword

export default signupSlice.reducer
